import InteractiveTable from 'components/InteractiveTable';
import { Column } from 'components/InteractiveTable';
import { ProcessedAssetHistory } from '../../helpers/processAssetFullHistory';
import { sumBy } from 'lodash';
import {
  dateToLabel,
  numberToCurrencyString,
  numberToPercentageString,
  numberToDisplayString,
} from 'further-ui/utils';
import { AssetHistoryType } from 'further-types/lp/asset';

type AssetHistoryTableProps = {
  assetHistory: Array<ProcessedAssetHistory>;
  showSummary?: boolean;
  showHoldingClassName?: boolean;
};

const AssetHistoryTypeLabels: Record<AssetHistoryType, string> = {
  [AssetHistoryType.Investment]: 'Investment',
  [AssetHistoryType.Revaluation]: 'Revaluation',
  [AssetHistoryType.Realisation]: 'Realisation',
};

const AssetHistoryTable: React.FC<AssetHistoryTableProps> = ({
  assetHistory,
  showSummary = true,
  showHoldingClassName = true,
}) => {
  const moreThanOneCurrency =
    assetHistory.length > 1 &&
    assetHistory.some(
      (entry) =>
        entry.currency !== undefined &&
        assetHistory[0].currency !== undefined &&
        entry.currency !== assetHistory[0].currency,
    );

  const formattedRows = assetHistory.map((entry) => ({
    ...entry,
    unrealisedValue: numberToCurrencyString(entry.unrealisedValue, {
      currency: entry.currency,
    }),
    unitPrice: numberToCurrencyString(entry.unitPrice, {
      currency: entry.currency,
      unlimitedDp: true,
    }),
    sharesHeld: numberToDisplayString(entry.sharesHeld),
    cumulativeSharesSold: numberToDisplayString(entry.cumulativeSharesSold),
    cumulativeRealisedValue: numberToCurrencyString(
      entry.cumulativeRealisedValue,
      {
        currency: entry.currency,
      },
    ),
    valuationChange:
      entry.valuationChange !== undefined
        ? numberToCurrencyString(entry.valuationChange, {
            currency: entry.currency,
          })
        : '-',
    valuationChangePercentage:
      entry.valuationChangePercentage !== undefined
        ? numberToPercentageString(entry.valuationChangePercentage)
        : '-',
  }));

  const unrealisedValueTotal = sumBy(assetHistory, 'unrealisedValue');
  const valuationChangeTotal = sumBy(assetHistory, 'valuationChange');
  const cumulativeRealisedValueTotal =
    assetHistory.length > 0
      ? assetHistory[assetHistory.length - 1].cumulativeRealisedValue
      : 0;

  const entriesWithChange = assetHistory.filter(
    (entry) => entry.valuationChange !== undefined,
  );
  const firstEntryWithChange = entriesWithChange[0];
  const lastEntry = assetHistory[assetHistory.length - 1];
  const totalPercentageChange =
    firstEntryWithChange && lastEntry
      ? (lastEntry.unrealisedValue -
          (firstEntryWithChange.unrealisedValue -
            (firstEntryWithChange.valuationChange ?? 0))) /
        (firstEntryWithChange.unrealisedValue -
          (firstEntryWithChange.valuationChange ?? 0))
      : 0;

  const columns: Array<Column> = [
    ...(showHoldingClassName
      ? [
          {
            headerName: 'Holding class',
            field: 'holdingClassName',
            flex: 1,
            sortable: false,
            summary: moreThanOneCurrency || !showSummary ? undefined : 'Totals',
          },
        ]
      : []),
    {
      headerName: 'Valuation date',
      field: 'date',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return dateToLabel(params.value);
      },
    },
    {
      headerName: 'Function',
      field: 'type',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return AssetHistoryTypeLabels[params.value as AssetHistoryType];
      },
    },
    {
      headerName: 'Unrealised value',
      field: 'unrealisedValue',
      flex: 1,
      sortable: false,
      summary:
        moreThanOneCurrency || !showSummary
          ? undefined
          : numberToCurrencyString(unrealisedValueTotal),
    },
    {
      headerName: 'Unit price',
      field: 'unitPrice',
      flex: 1,
      sortable: false,
    },
    {
      headerName: 'Units held',
      field: 'sharesHeld',
      flex: 1,
      sortable: false,
    },
    {
      headerName: 'Valuation change',
      field: 'valuationChange',
      flex: 1,
      sortable: false,
      summary:
        moreThanOneCurrency || !showSummary
          ? undefined
          : numberToCurrencyString(valuationChangeTotal),
    },
    {
      headerName: 'Valuation change (%)',
      field: 'valuationChangePercentage',
      flex: 1,
      sortable: false,
      summary:
        moreThanOneCurrency || !showSummary
          ? undefined
          : numberToPercentageString(totalPercentageChange),
    },
    {
      headerName: 'Units sold',
      field: 'cumulativeSharesSold',
      flex: 1,
      sortable: false,
    },
    {
      headerName: 'Realised value',
      field: 'cumulativeRealisedValue',
      flex: 1,
      sortable: false,
      summary:
        moreThanOneCurrency || !showSummary
          ? undefined
          : numberToCurrencyString(cumulativeRealisedValueTotal),
    },
  ];

  return (
    <InteractiveTable
      id="asset-history-table"
      rows={formattedRows}
      columns={columns}
      loading={false}
    />
  );
};

export default AssetHistoryTable;
